/* eslint-disable no-underscore-dangle, no-undef */
import React from 'react';
import { Provider } from 'react-redux';

import store from '../../../../../ui-kitchen/src/reducers/createStore';
import { focusTrap, getQueryVariable } from '../../../../../ui-kitchen/src/utils';
import WidgetAppContainer from './WidgetAppContainer';
import FocusTrapContext, { initFocusTrap } from '../../contexts/focusTrap';
import PathBuilderContext from './PathBuilderContext';
import Logger from '../../../../../ui-kitchen/src/utils/logger';
import { IconLoader } from '../../../../../ui-kitchen/src/components/Icon';
import { sanitizeString } from '../../../../../ui-kitchen/src/utils/sanitizeString';
import getEnvConfigs from '../../../scripts/getEnvConfigs';
import { setApiConfig } from '../../../../../ui-kitchen/src/api';
import PathBuilderFactory from '../../utils/pathBuilder';
import { initHeightCheck } from '../../../scripts/utils';
import momentLocaleFormats from '../../utils/i18n/momentLocaleFormats';
import getLocale, { DEFAULT_LANGUAGE_CODE, DEFAULT_LOCALE, getLanguageCodeFromLocale } from '../../utils/i18n/getLocale';
import i18nLoaderReact from '../../utils/i18n/i18nLoaderReact';

import '../../../../../../node_modules/normalize.css/normalize.css';
import '../../../../../ui-kitchen/src/style/font-definitions.scss';

// Initialization block ahead of render
// These items come over from Angular app.js and react2angular config

// monitor height changes to send to parent window
initHeightCheck();

// Set up environment-specific configurations from envConfig object,
// which has been migrated from Angular constants and webpack loading.
// Eventually migrate to React context provider.
const {
  AutomationMode, Config, AnnotationServiceConfig, ResyosServiceConfig, ImageServiceConfig, ItemsServiceConfig, ResyAmexConfig,
} = getEnvConfigs();

// sets config values for api in ui-kitchen
const ConfigCopy = { ...Config };
if (AutomationMode) {
  ConfigCopy.apiKey = ConfigCopy.automationApiKey;
}
ConfigCopy.apiKey = getQueryVariable('apiKey') || ConfigCopy.apiKey;
setApiConfig({
  Config: ConfigCopy,
  AnnotationServiceConfig,
  ResyosServiceConfig,
  ImageServiceConfig,
  ItemsServiceConfig,
  ResyAmexConfig,
});

const PathBuilder = PathBuilderFactory({});

initFocusTrap(focusTrap);

// Set up the referrer for Amplitude analytics
const locationQuery = window.location.search.split('?')[1];
window.__REFERRER__ = new URLSearchParams(locationQuery).get('referrerPath') || null;

window.__DESCRIPTION__ = __DESCRIPTION__;
window.__VERSION__ = __VERSION__;
window.__SHA__ = __SHA__;

// i18n: locale setup
// window.moment = moment; // persist in vendor.js for now.
moment.locale(DEFAULT_LANGUAGE_CODE);
const locale = getLocale();
// dispatch(setLocale(locale)); // move to WidgetAppContainer
i18nLoaderReact.load({
  prefix: '../../locale/strings/',
  key: locale,
  suffix: '.json',
});
// used for tagging in mixpanel and simon
window.preferredLanguage = locale || DEFAULT_LOCALE;

// i18n: languageCode setup
const languageCode = getLanguageCodeFromLocale(locale);
const momentFormat = momentLocaleFormats[languageCode] || momentLocaleFormats[DEFAULT_LANGUAGE_CODE];
moment.locale(languageCode, momentFormat);
window.resyLanguage = languageCode;

IconLoader((name) => {
  const sanitizedName = sanitizeString(name);
  // eslint-disable-next-line no-unsanitized/method
  return import(/* webpackChunkName: "[request]" */ `../../svg/dynamic/${sanitizedName}.svg`)
    .then(
      (module) => module.default,
      () => Logger.error(`[ERROR] Icon ${sanitizedName} does not exist`)
    );
});

// Migration notes: This does not function due to cross-origin issue.
// At time of final React cutover, decide if this should still load or not.
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape' || e.key === 'Esc' || e.keyCode === 27) {
    try {
      window.top.resyWidget.closeModal();
    } catch {
      Logger.error('The parent is not accessible. ESC will not close the modal.');
    }
  }
});

// – <WidgetApp /> is the root of the React component tree. It has no visibility to the Redux store or to ReactRouter context.
//  |
//  |— <WidgetAppContainer /> and children have visibility to the Redux store, but not to the ReactRouter context
//    |
//    |— <WidgetRouteOutlet /> and children have visibility to the Redux store and to the ReactRouter context

const WidgetApp = () => (
  <Provider store={store}>
    <PathBuilderContext.Provider value={PathBuilder}>
      <FocusTrapContext.Provider value={focusTrap}>
        <WidgetAppContainer locale={locale} />
      </FocusTrapContext.Provider>
    </PathBuilderContext.Provider>
  </Provider>
);

export default WidgetApp;
